import { Component, computed, inject, input, output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import type { BreadcrumbDropdownMenuItem } from '../../breadcrumb.interfaces';
import { HlmPopoverImports } from '@spartan-ng/helm/popover';
import { BreadcrumbStoreService } from '../../services/breadcrumb-store.service';
import { BreadcrumbDropdownItemStore } from './breadcrumb-dropdown-item.store';
import { ThemedIconComponent } from '../../../../atoms';
import { SidebarListComponent } from '../../../sidebar-list/sidebar-list.component';
import {
  SidebarListFooterAction,
  SidebarListItem,
  SidebarListVariant,
} from '../../../sidebar-list/sidebar-list.interfaces';

@Component({
  selector: 'ocp-breadcrumb-dropdown-item',
  styles: `
    :host {
      display: flex;
      align-items: center;
      cursor: pointer;
      gap: 0.3rem;
      min-width: 0;
    }

    .breadcrumb-dropdown-label {
      min-width: 0;
      flex: 1;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  `,
  imports: [HlmPopoverImports, ThemedIconComponent, SidebarListComponent],
  template: `
    <span class="breadcrumb-dropdown-label">{{ label() }}</span>

    <ocp-themed-icon
      (click)="onClickDropdownMenu($event)"
      class="breadcrumb__dropdown-icon breadcrumb__menu-trigger"
      width="10"
      height="10"
      name="ArrowHeadDown.svg"
    />

    <hlm-popover
      [attachTo]="activeTrigger()"
      [state]="overlayState()"
      (stateChanged)="onStateChanged($event)"
    >
      <hlm-popover-content *hlmPopoverPortal>
        <ocp-sidebar-list
          [variant]="dropdownVariant()"
          [items]="mappedDropdownMenuItems()"
          [showSelectionIndicator]="false"
          (itemClicked)="onItemClick($event)"
          (footerActionClicked)="onFooterActionClicked()"
          [footerActions]="footerActions()"
          [showCreateInHeader]="true"
          [showCreate]="false"
        />
      </hlm-popover-content>
    </hlm-popover>
  `,
})
export class BreadcrumbDropdownItemComponent {
  private readonly activatedRoute = inject(ActivatedRoute);

  protected readonly isOpen = signal(false);
  protected readonly overlayState = signal<'open' | 'closed' | null>(null);
  /** Tracks the clicked icon so the popover anchors to it */
  protected readonly activeTrigger = signal<HTMLElement | null>(null);

  readonly label = input<string>('');
  readonly dropdownVariant = input<SidebarListVariant>('compact');
  readonly footerActions = input<SidebarListFooterAction[]>([]);
  readonly dropdownMenuClick = output<void>();
  readonly footerActionClicked = output<void>();
  protected readonly breadcrumbStoreService = inject(BreadcrumbStoreService);
  protected readonly breadcrumbDropdownItemStore = inject(BreadcrumbDropdownItemStore, {
    optional: true,
  });

  protected readonly currentQueryParams = toSignal(this.activatedRoute.queryParams, {
    initialValue: this.activatedRoute.snapshot.queryParams,
  });

  protected readonly dropdownMenuItems = computed(() => {
    if (this.breadcrumbDropdownItemStore?.dropdownMenuItems()) {
      return this.breadcrumbDropdownItemStore.dropdownMenuItems();
    }
    return this.breadcrumbStoreService.dropdownMenuItems();
  });

  protected readonly mappedDropdownMenuItems = computed<SidebarListItem[]>(() => {
    const idOccurrences = new Map<string, number>();
    const selectedItemRoute = this.breadcrumbDropdownItemStore?.selectedItemRoute() ?? null;
    const currentQueryParams = this.currentQueryParams();

    return this.dropdownMenuItems().map(item => {
      const sourceId = this.resolveSourceItemId(item);
      const occurrence = idOccurrences.get(sourceId) ?? 0;
      idOccurrences.set(sourceId, occurrence + 1);

      const uniqueSourceId = occurrence === 0 ? sourceId : `${sourceId}__${occurrence}`;
      const itemRoute = this.normalizeRouterLink(item.routerLink);
      const selected =
        selectedItemRoute !== null && itemRoute
          ? selectedItemRoute === itemRoute
          : ((item as SidebarListItem).selected ?? false);

      return {
        ...item,
        id: `dropdown-${uniqueSourceId}`,
        label: item.label ?? '',
        initial: item.label ?? '',
        selected,
        queryParams: currentQueryParams,
        queryParamsHandling: 'merge',
      } satisfies SidebarListItem;
    });
  });

  private resolveSourceItemId(item: BreadcrumbDropdownMenuItem): string {
    const explicitId = item.id?.trim();
    if (explicitId) {
      return `id-${explicitId}`;
    }

    const route = this.normalizeRouterLink(item.routerLink);
    if (route) {
      return `route-${route}`;
    }

    const label = item.label?.trim();
    if (label) {
      return `label-${label}`;
    }

    return 'item';
  }

  private normalizeRouterLink(
    routerLink: string | readonly (string | number)[] | undefined
  ): string {
    if (Array.isArray(routerLink)) {
      return routerLink.map(String).join('/');
    }
    if (typeof routerLink === 'string') {
      return routerLink;
    }
    return '';
  }

  constructor() {
    if (this.breadcrumbStoreService.selectedDropdownItem()) {
      this.onItemClick(this.breadcrumbStoreService.selectedDropdownItem()!);
    }
  }

  private closePopover(): void {
    this.overlayState.set('closed');
  }

  protected onStateChanged(state: 'open' | 'closed'): void {
    if (state === 'closed') {
      this.overlayState.set(null);
    }
    this.isOpen.set(state === 'open');
  }

  protected onItemClick(item: SidebarListItem): void {
    this.breadcrumbStoreService.selectedDropdownItem.set(item);
    this.closePopover();
    if (this.breadcrumbDropdownItemStore?.dropdownMenuItems().length) {
      this.breadcrumbDropdownItemStore.selectedItemRoute.set(
        this.normalizeRouterLink(item.routerLink)
      );
      return;
    }
    this.breadcrumbStoreService.dropdownMenuItems.update(items => {
      return items.map(i => ({
        ...i,
        selected: this.isSelectedMenuItem(i, item),
      }));
    });
  }

  private isSelectedMenuItem(
    sourceItem: BreadcrumbDropdownMenuItem,
    selectedItem: SidebarListItem
  ): boolean {
    const sourceRoute = this.normalizeRouterLink(sourceItem.routerLink);
    const selectedRoute = this.normalizeRouterLink(selectedItem.routerLink);

    if (sourceRoute || selectedRoute) {
      return sourceRoute === selectedRoute;
    }

    return (sourceItem.label ?? '') === (selectedItem.label ?? '');
  }

  protected onClickDropdownMenu(event: Event): void {
    if (this.isOpen()) {
      this.closePopover();
    } else {
      this.activeTrigger.set(event.currentTarget as HTMLElement);
      this.overlayState.set('open');
    }
    this.dropdownMenuClick.emit();
  }

  protected onFooterActionClicked(): void {
    this.footerActionClicked.emit();
  }
}
